import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Sparkles } from "lucide-react";

/** Modal editor for a company's arbitrary custom fields + webhook URLs. */
export default function CompanyEditor({ company, open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [slack, setSlack] = useState("");
  const [teams, setTeams] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !company) return;
    const cf = company.custom_fields || {};
    setRows(Object.entries(cf).map(([k, v]) => ({ key: k, value: typeof v === "string" ? v : JSON.stringify(v) })));
    setSlack(company.slack_webhook_url || "");
    setTeams(company.teams_webhook_url || "");
  }, [open, company]);

  const addRow = () => setRows((r) => [...r, { key: "", value: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, x) => x !== i));
  const setKey = (i, k) => setRows((r) => r.map((x, xi) => (xi === i ? { ...x, key: k } : x)));
  const setValue = (i, v) => setRows((r) => r.map((x, xi) => (xi === i ? { ...x, value: v } : x)));

  const save = async () => {
    setBusy(true);
    try {
      const cf = {};
      for (const row of rows) {
        const k = (row.key || "").trim();
        if (!k) continue;
        cf[k] = row.value;
      }
      await api.patch(`/companies/${company.id}`, {
        custom_fields: cf,
        slack_webhook_url: slack,
        teams_webhook_url: teams,
      });
      toast.success("Company updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  if (!company) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-brand" />
            {company.trading_name || company.legal_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Custom fields</div>
            <p className="text-xs text-muted-foreground">Arbitrary key/value metadata attached to this company workspace.</p>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center" data-testid={`cf-row-${i}`}>
                <Input value={r.key} onChange={(e) => setKey(i, e.target.value)} placeholder="Key (e.g. account_id)" className="rounded-lg h-9" data-testid={`cf-key-${i}`} />
                <Input value={r.value} onChange={(e) => setValue(i, e.target.value)} placeholder="Value" className="rounded-lg h-9" data-testid={`cf-value-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => removeRow(i)} data-testid={`cf-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-lg" onClick={addRow} data-testid="cf-add-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add field
            </Button>
          </div>

          <div className="space-y-3 pt-2 border-t border-border">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Integrations</div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Slack incoming webhook URL</Label>
              <Input value={slack} onChange={(e) => setSlack(e.target.value)} placeholder="https://hooks.slack.com/services/…" className="rounded-lg" data-testid="slack-webhook-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Microsoft Teams incoming webhook URL</Label>
              <Input value={teams} onChange={(e) => setTeams(e.target.value)} placeholder="https://outlook.office.com/webhook/…" className="rounded-lg" data-testid="teams-webhook-input" />
            </div>
            <p className="text-[11px] text-muted-foreground">When new approval requests, quota alerts, or bulk-send completions occur, we&apos;ll POST a message to these URLs.</p>
          </div>
        </div>
        <DialogFooter>
          <Button className="rounded-xl" onClick={save} disabled={busy} data-testid="company-editor-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
